import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { useState, type FormEvent } from 'react';
import { api, describeError } from './api.js';
import { RingOrb } from './RingOrb.js';

function defaultDeviceName(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android phone';
  if (/Windows/.test(ua)) return 'Windows PC';
  if (/Mac OS/.test(ua)) return 'Mac';
  return 'This device';
}

/**
 * Sign in with a passkey, or set one up with a one-time code from the terminal
 * (`passkey new` on the server, or from Claude Code).
 */
export function SignIn({
  registered,
  onSignedIn,
}: {
  registered: boolean;
  onSignedIn: () => void;
}) {
  const [mode, setMode] = useState<'login' | 'setup'>(registered ? 'login' : 'setup');
  const [code, setCode] = useState('');
  const [name, setName] = useState(defaultDeviceName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const supported = browserSupportsWebAuthn();

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onSignedIn();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitSetup = (e: FormEvent) => {
    e.preventDefault();
    void run(() => api.register(name.trim() || 'Passkey', code.trim()));
  };

  return (
    <div className="center-screen">
      <RingOrb state={busy ? 'thinking' : 'idle'} showLabel={false} />
      <h1 className="title">Alfred</h1>

      {!supported && (
        <p className="error">
          This browser doesn’t support passkeys. Try Chrome, Edge, Safari or Brave.
        </p>
      )}

      {mode === 'login' ? (
        <div className="card narrow">
          <button
            className="btn btn-primary btn-wide"
            disabled={busy || !supported}
            onClick={() => void run(api.login)}
          >
            {busy ? 'Waiting for your passkey…' : 'Sign in with passkey'}
          </button>
          <button className="link-btn" onClick={() => setMode('setup')}>
            New device? Set it up with a code
          </button>
        </div>
      ) : (
        <form className="card narrow" onSubmit={submitSetup}>
          <p className="muted small">
            {registered ? 'Add this device' : 'First-time setup'}: enter a one-time setup code. Get
            one by running <code>passkey new</code> on Alfred’s server (or ask Claude Code for one).
          </p>
          <label className="field">
            <span>Setup code</span>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="XXXXX-XXXXX"
              autoComplete="one-time-code"
              autoCapitalize="characters"
              spellCheck={false}
              required
            />
          </label>
          <label className="field">
            <span>Name this device</span>
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />
          </label>
          <button
            className="btn btn-primary btn-wide"
            disabled={busy || !supported || !code.trim()}
          >
            {busy ? 'Creating passkey…' : 'Create passkey'}
          </button>
          {registered && (
            <button type="button" className="link-btn" onClick={() => setMode('login')}>
              Back to sign in
            </button>
          )}
        </form>
      )}

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
